/**
 * Created by Ning Jiang on 8/31/2017.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Elastic, TimelineLite, TweenLite} from "gsap";
import {BarbarianFuryGameConfig} from "../BarbarianFuryGameConfig";
import {BFGameEvent} from "../events/BFGameEvent";
import {BarbarianFuryGameMode} from "../gamemode/BarbarianFuryGameMode";
import {BarbarianFuryParsedGameData} from "../server/data/BarbarianFuryParsedGameData";
import {MHCounterViewData, MultiplierHolderCounterView} from "./MultiplierHolderCounterView";

export class MultiplierIndicator extends BaseView {

    public static readonly UPDATE_MULTIPLIER:string = "MultiplierEvent_UpdateMultiplier";

    private _currentMultiplier:number;
    private _layer:PIXI.Container;
    private _foregroundLayer:PIXI.Container;
    private _multiplierStripeObject:MultiplierHolderCounterView;
    private _isWin:boolean;
    private _gameMode:string = BarbarianFuryGameMode.NORMAL;
    private _nextMode:string = BarbarianFuryGameMode.NORMAL;
    private _isRestoreState:boolean;
    private _multiplierBg:PIXI.Sprite;
    private _prevMultiplier:number = 0;
    private _xImage:PIXI.Sprite;
    private _highlighedFrame:PIXI.Sprite;
    private _container:PIXI.Container;
    private _gameData:BarbarianFuryParsedGameData | null;
    private _isGameInFS:boolean = false;

    private _innerFireAnimation:TimelineSprite;
    private _blastAnimation:TimelineSprite;

    constructor() {
        super();

        this._currentMultiplier = 1;
        this._layer = StageManager.getLayer(BarbarianFuryGameConfig.instance.LAYERS.MULTIPLIER.name);
        this._foregroundLayer = StageManager.getLayer(BarbarianFuryGameConfig.instance.LAYERS.FOREGROUND.name);

        let nVDataObject:MHCounterViewData = {
            imageAssetPrefix : "mult_fr_",
            digits : 3,
            value : 1,
            imageLeftMargin : 0,
            imageTopMargin : 0,
            frameHeight : 100,
            frameWidth : 66,
            digitPadding : 0,
            animationDuration : 0,
            maskHeight : 115,
            maskWidth : 250,
            maskX : -20,
            maskY : -15
        };
        this._multiplierStripeObject = new MultiplierHolderCounterView(nVDataObject);
        this.addChild(this._multiplierStripeObject);
    }

    protected addEventListeners():void {
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event:GameEvent) => this.onServerDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event:GameEvent) => this.onServerDataParsed(event.params[0]));
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, () => this.onSpinStart());
        EventHandler.addEventListener(this, BFGameEvent.ENTER_FREESPIN, () => this.enterInFreeSpin());
        EventHandler.addEventListener(this, BFGameEvent.EXIT_FREESPIN, () => this.outOfFreeSpin());
        EventHandler.addEventListener(this, MultiplierIndicator.UPDATE_MULTIPLIER, (event:GameEvent) => this.onUpdate(event.params[0]));
        EventHandler.addEventListener(this, BFGameEvent.UPDATE_MULTIPLIER_BG, (event:GameEvent) => this.updateMultiplierBG(event.params[0]));
        EventHandler.addEventListener(this, BFGameEvent.TOGGLE_MH_INNER_FIRE, (event:GameEvent) => this.toggleInnerFire(event.params[0]));
    }

    private toggleInnerFire(displayFlag:boolean) {
        let timer:TimelineLite = new TimelineLite({
            paused : true,
            onComplete : () => {
                timer.kill();
            }
        });

        let fadeInDuration:number = 1,
            fadeOutDuration:number = 2;

        if(displayFlag) {
            timer.add(() => {
                this._innerFireAnimation.visible = true;
                this._innerFireAnimation.playLoop();
                TweenLite.fromTo(this._innerFireAnimation, fadeInDuration, {alpha : 0}, {alpha : 1});
            }, 0);
        } else {
            timer.add(() => {
                TweenLite.fromTo(this._innerFireAnimation, fadeOutDuration, {alpha : 1}, {
                    alpha : 0,
                    onComplete : () => {
                        this.hideMultiplierInnerFire();
                    }
                });
            }, 0);
        }

        timer.play(0);
    }

    private hideMultiplierInnerFire():void {
        this._innerFireAnimation.alpha = 0;
        this._innerFireAnimation.visible = false;
        this._innerFireAnimation.stopLoop();

        this._highlighedFrame.alpha = 0;

        this._blastAnimation.visible = false;
    }

    protected initAnimations():void {
        this._container = new PIXI.Container();
        this._container.pivot.set(0.5);

        this._multiplierBg = new PIXI.Sprite(GameResources.getTextures("mainGameMultiplierBg")[0]);

        this._highlighedFrame = new PIXI.Sprite(GameResources.getTextures("multiplierUpFrame")[0]);
        this._highlighedFrame.anchor.set(0.5);
        this._highlighedFrame.position.set(64, 43);
        this._highlighedFrame.alpha = 0;

        this._innerFireAnimation = new TimelineSprite(GameResources.getTextures("innerFire"), 20);
        this._innerFireAnimation.blendMode = PIXI.BLEND_MODES.ADD;
        this._innerFireAnimation.alpha = 0;
        this._innerFireAnimation.visible = false;

        this._blastAnimation = new TimelineSprite(GameResources.getTextures("multiplierBlast"), 20);
        this._blastAnimation.position.set(-35, -60);
        this._blastAnimation.visible = false;
        this._blastAnimation.blendMode = PIXI.BLEND_MODES.ADD;

        this.addChild(this._multiplierBg);
        this.addChild(this._highlighedFrame);
        this.addChild(this._innerFireAnimation);
        this.addChild(this._blastAnimation);

        this._container.scale.set(1);
        this._container.position.set(0, 0);
        this._multiplierStripeObject.scale.set(0.55);
        this._multiplierStripeObject.position.set(4, 22);

        this._xImage = new PIXI.Sprite(GameResources.getTextures("mult_fr_x")[0]);
        this._xImage.anchor.set(0.5);
        this._xImage.position.set(30, 53);
        this._xImage.scale.set(0.4);

        this._container.addChild(this._xImage);
        this._container.addChild(this._multiplierStripeObject);

        this.addChild(this._container);

        this._layer.addChild(this);

        this.updateText();
    }

    protected updateMultiplierBG(src:string):void {
        this._multiplierBg.texture = GameResources.getTextures(src)[0];
        this._isGameInFS = src.indexOf("bonus") > -1;
    }

    protected onSpinStart():void {
        this.hideMultiplierInnerFire();
        this._currentMultiplier = this._currentMultiplier > 1 ? this._currentMultiplier : 1;
        if(this._nextMode === BarbarianFuryGameMode.NORMAL) {
            this._currentMultiplier = 1;
        }

        // #218 => race condition of ReelEvent.ALL_REELS_SPIN_STARTED with ServerEvent.GAME_DATA_PARSED.
        this.updateText();
    }

    protected onResize(resizeData:IResizeData):void {
        this.scale.set(1);
        if(resizeData.orientation === "landscape") {
            this.position.set(1032, 55);
            this._layer.position.set(-280, 0);

            // fix for 4:3 ratio
            if(resizeData.bottom === 620 && resizeData.width <= 970) {
                this.position.set(1004, 55);
                this.scale.set(0.9);
            }
        } else {
            this.position.set(590, 281);
            this._layer.position.set(0, -280);
        }
    }

    private enterInFreeSpin():void {
        //fix for #186
        this._isGameInFS = true;
        this._currentMultiplier = 1;
        this.updateText();
    }

    private outOfFreeSpin():void {
        this._isGameInFS = false;
        this._currentMultiplier = 1;
        const timer:TimelineLite = new TimelineLite();
        this.updateText();
    }

    private onServerDataParsed(data:BarbarianFuryParsedGameData):void {
        this._gameData = data;
        this._isWin = data.singleWin > 0;
        this._isRestoreState = data!.isRestoreState;
        this._gameMode = data.mode;
        this._nextMode = data.nextMode;
        if(data.freeSpinMultiplier) {
            this._currentMultiplier = data.freeSpinMultiplier;
        }
    }

    private updateText():void {
        this.positionMultiplierDigits(this._currentMultiplier);
    }

    private onUpdate(number?:number):void {
        if(number == null) {
            this._currentMultiplier++;
        } else {
            this._currentMultiplier = number;
        }

        this.updateText();
    }

    private positionMultiplierDigits(currMultiplier:number):void {
        if(currMultiplier > 1 || (this._nextMode.indexOf(BarbarianFuryGameMode.FREESPIN) > -1)) {
            if(this._prevMultiplier !== this._currentMultiplier) {
                this._blastAnimation.getAnimationAutoShowHide(true, true, [19], () => {
                    if(this._blastAnimation.currentFrame === 0) {
                        const timer:TimelineLite = new TimelineLite({
                            paused : true,
                            onComplete : () => {
                                timer.kill();
                            }
                        });
                        timer.add(() => {
                            TweenLite.fromTo(this._highlighedFrame, 0.5, {alpha : 0}, {alpha : 1});
                        }, 0);
                        timer.add(() => {
                            TweenLite.fromTo(this._container.scale, 0.3, {x : 1, y : 1}, {
                                x : 1.3,
                                y : 1.3,
                                ease : Elastic.easeInOut.config(2, 1)
                            });
                            TweenLite.fromTo(this._container.position, 0.3, {x : 0, y : 0}, {
                                x : -20,
                                y : -13,
                                ease : Elastic.easeInOut.config(2, 1)
                            });
                        }, 0);
                        timer.add(() => {
                            TweenLite.fromTo(this._container.scale, 0.3, {x : 1.3, y : 1.3}, {
                                x : 1,
                                y : 1,
                                ease : Elastic.easeInOut.config(2, 1)
                            });
                            TweenLite.fromTo(this._container.position, 0.3, {x : -20, y : -13}, {
                                x : 0,
                                y : 0,
                                ease : Elastic.easeInOut.config(2, 1)
                            });
                        }, 0.3);
                        timer.add(() => {
                            TweenLite.fromTo(this._highlighedFrame, 0.5, {alpha : 1}, {alpha : 0});
                        }, 0.6);
                        timer.play(0);
                    }
                });

                this._blastAnimation.getAnimationAutoShowHide(true, true);
            }
            this.toggleMultiplierHolderText(true);
        } else {
            this.toggleMultiplierHolderText(false);
        }

        this._multiplierStripeObject.setCounterValue(currMultiplier);
        this.toggleHiddenDigit(currMultiplier);
        this._prevMultiplier = this._currentMultiplier;
    }

    private toggleMultiplierHolderText(display:boolean):void {
        let animPos = display ? 0 : this._multiplierStripeObject._animationSpeed;

        const timer:TimelineLite = new TimelineLite({
            paused : true,
            onComplete : () => {
                timer.kill();
            }
        });
        timer.add(() => {
            //fix for #136
            this.visible = display;
            this._xImage.visible = display;
            this._multiplierStripeObject.visible = display;
        }, animPos);
        timer.play(0);
    }

    private toggleHiddenDigit(count:number):void {
        let firstHiddenDigit = Math.floor(count / 100 % 100);

        if(count > 99) {
            this._container.scale.set(0.75);
            this._container.position.set(2, 10);
            this._multiplierStripeObject._digitArr[0].container.visible = true;
            this._multiplierStripeObject.position.set(40, 22);
        } else {
            if(this._multiplierStripeObject._digitArr[0].container.visible) {
                this._container.scale.set(1);
                this._container.position.set(0, 0);
                this._multiplierStripeObject._digitArr[0].container.visible = false;
                this._multiplierStripeObject.position.set(4, 22);
            }
        }
    }
}